import get from 'lodash.get'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { CircularProgress } from '@material-ui/core'
import { ICON_NAMES } from '../../../../constants'
import { useCSV } from '../../../../hooks'
import Icon from '../../../atoms/Icon'
import Text from '../../../atoms/Text'
import SydButton from '../../../commonDesign/Button'
import SydInput from '../../../commonDesign/SydInput'
import SydLabel from '../../../commonDesign/SydLabel'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import { defaultGetSubRows } from '../../DenaliAnalysis/tables/presentation'

export const ExportTableToggle = ({
  pipeline,
  tableData,
  columns,
  getSubRows = defaultGetSubRows,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { downloadCSVFile } = useCSV()

  const {
    reset,
    control,
    register,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: {
      exportFileName: pipeline + '_export'
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const handleClose = useCallback(() => {
    reset()
    setIsOpen(false)
  }, [reset])

  const onSubmit = useCallback(
    ({ exportFileName }) => {
      if (!tableData?.length) return

      const sanitizeFilename = (name) => {
        if (!name) return `${pipeline ?? 'table'}_export`
        const cleaned = (name ?? '')
          .replace(/[^a-zA-Z0-9-_]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 100)

        return cleaned || `${pipeline ?? 'table'}_export`
      }

      const fileName = `${sanitizeFilename(exportFileName)}.csv`

      const headers = columns
        .filter(col => !col.isGroup)
        .map((col) => {
          let header = col.header || col.Header || col.accessorKey || col.accessor
          if (typeof header === 'function') {
            header = header()
          }
          return header ? String(header) : ''
        })

      const flattenRowRecursively = (row = {}) => {
        const subRows = getSubRows(row) || []
        if (subRows.length > 0) {
          return [
            row,
            ...subRows.flatMap((subRow) => flattenRowRecursively(subRow, row))
          ]
        }
        return row
      }
      const flatData = tableData.flatMap((row) => flattenRowRecursively(row))
      const exportColumns = columns.filter(col => !col.isGroup)

      const dataRows = flatData.map((row) =>
        exportColumns.map((col) => {
          if (typeof col.accessor === 'function') {
            return col.accessor(row)
          }
          const accessor = col.accessorKey || col.accessor
          return accessor ? get(row, accessor) : null
        })
      )

      downloadCSVFile({
        data: dataRows,
        headers,
        fileName
      })

      setIsOpen(false)
    },
    [tableData, pipeline, columns, downloadCSVFile, getSubRows]
  )

  return (
    <>
      <SydButton
        onClick={() => setIsOpen(true)}
        title='Export Table'
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size='16px' /> : <Icon name={ICON_NAMES.download} />}
      </SydButton>

      <SydModal
        title='Export Table'
        open={isOpen}
        onClose={handleClose}
        size='small'
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='mb-4'>
            <SydLabel label='File Name' required>
              <Controller
                name='exportFileName'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className='flex items-center gap-sm'>
                    <div className='flex-1'>
                      <SydInput
                        {...field}
                        placeholder='file name'
                        ref={register('exportFileName').ref}
                        error={errors?.exportFileName}
                      />
                    </div>
                    <div>
                      <Text text='.csv' customFontSize={16} lineHeight='20px' />
                    </div>
                  </div>
                )}
              />
            </SydLabel>
          </div>

          <SydModalActions>
            <SydButton variant='secondary' onClick={handleClose}>
              Cancel
            </SydButton>
            <SydButton variant='primary' type='submit'>
              Export
            </SydButton>
          </SydModalActions>
        </form>
      </SydModal>
    </>
  )
}

ExportTableToggle.propTypes = {
  pipeline: PropTypes.string.isRequired,
  tableData: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  getSubRows: PropTypes.func,
  isLoading: PropTypes.bool
}
